/**
 * Campaign Analytics — performance dashboard across all active + completed
 * campaigns. Single read of /api/campaigns/analytics/overview, presented as:
 *   · 4 momentum metric tiles (assets / published / share clicks / 30d created)
 *   · 14-day timeseries sparkline (created vs published vs clicks)
 *   · Per-platform breakdown bars
 *   · Recent campaign cards (deep-link to workspace)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ChartLineUp, FileText, PaperPlaneTilt, CursorClick, Briefcase, ArrowRight, Lightning,
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const LIME = '#c8f060';
const SKY = '#64d2ff';

const formatDayLabel = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return iso; }
};

const MetricTile = ({ label, value, sub, accent, icon: Icon }) => (
  <div className="p-5 rounded-2xl" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)' }}>
    <div className="flex items-center gap-2 mb-3" style={{ color: accent || 'rgba(245,240,232,0.5)' }}>
      <Icon size={14} weight="fill" />
      <p className="text-[10px] tracking-[0.22em] uppercase font-bold">{label}</p>
    </div>
    <p className="text-4xl font-semibold tabular-nums leading-none" style={{ fontFamily: "'Instrument Serif', serif", color: '#fff' }}>{value}</p>
    {sub && <p className="text-[11px] mt-2" style={{ color: 'rgba(245,240,232,0.45)' }}>{sub}</p>}
  </div>
);

const Sparkline = ({ days }) => {
  const maxCreated = Math.max(1, ...days.map(d => d.created));
  const maxPub = Math.max(1, ...days.map(d => d.published));
  const maxClick = Math.max(1, ...days.map(d => d.clicks));
  const maxAll = Math.max(maxCreated, maxPub, maxClick);
  const w = 800, h = 160, pad = 8;
  const x = (i) => pad + (i * (w - 2 * pad)) / Math.max(1, days.length - 1);
  const y = (v) => h - pad - ((v / maxAll) * (h - 2 * pad));
  const pathFor = (key) =>
    days.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(d[key])}`).join(' ');
  return (
    <div className="rounded-2xl p-5" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: 'rgba(245,240,232,0.5)' }}>Last 14 days</p>
        <div className="flex gap-3 text-[10px]">
          <span className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, background: LIME, borderRadius: 2 }} /> Created</span>
          <span className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, background: SKY, borderRadius: 2 }} /> Published</span>
          <span className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, background: '#ff9a76', borderRadius: 2 }} /> Clicks</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height: 160 }}>
        <path d={pathFor('created')}   fill="none" stroke={LIME}     strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <path d={pathFor('published')} fill="none" stroke={SKY}      strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <path d={pathFor('clicks')}    fill="none" stroke="#ff9a76"  strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {days.map((d, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(d.created)}   r="2.5" fill={LIME} />
            <circle cx={x(i)} cy={y(d.published)} r="2.5" fill={SKY} />
            <circle cx={x(i)} cy={y(d.clicks)}    r="2.5" fill="#ff9a76" />
          </g>
        ))}
      </svg>
      <div className="flex justify-between mt-2 text-[9px]" style={{ color: 'rgba(245,240,232,0.4)' }}>
        <span>{formatDayLabel(days[0]?.date)}</span>
        <span>{formatDayLabel(days[Math.floor(days.length / 2)]?.date)}</span>
        <span>{formatDayLabel(days[days.length - 1]?.date)}</span>
      </div>
    </div>
  );
};

const PlatformBars = ({ rows }) => {
  if (!rows?.length) return null;
  const maxTotal = Math.max(1, ...rows.map(r => r.total));
  return (
    <div className="rounded-2xl p-5" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-4" style={{ color: 'rgba(245,240,232,0.5)' }}>By platform</p>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.platform} className="grid grid-cols-[100px,1fr,60px] gap-3 items-center">
            <span className="text-[12px] font-semibold truncate" style={{ color: '#fff' }}>{r.platform}</span>
            <div className="h-2 rounded-full overflow-hidden flex" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <span style={{ width: `${(r.total / maxTotal) * 100}%`, background: LIME, opacity: 0.45 }} />
              <span style={{ width: `${(r.published / maxTotal) * 100}%`, background: SKY, marginLeft: -((r.published / maxTotal) * 100) + '%' }} />
            </div>
            <span className="text-[11px] tabular-nums text-right" style={{ color: 'rgba(245,240,232,0.65)' }}>
              {r.published}/{r.total}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CampaignCard = ({ c }) => (
  <Link to={`/dashboard/campaign/${c.id}`} data-testid={`analytics-campaign-${c.id}`}
    className="block p-4 rounded-2xl transition hover:opacity-95"
    style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)', borderLeft: `2px solid ${c.status === 'active' ? LIME : '#444'}` }}>
    <div className="flex items-start justify-between mb-2">
      <p className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: c.status === 'active' ? LIME : 'rgba(245,240,232,0.45)' }}>
        {c.status} · {(c.objective || '').replace(/_/g, ' ')}
      </p>
      {typeof c.confidence_score === 'number' && (
        <span className="text-[10px] font-bold tabular-nums" style={{ color: c.confidence_score >= 85 ? LIME : '#f6c453' }}>{c.confidence_score}%</span>
      )}
    </div>
    <h3 className="text-lg leading-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif", color: '#fff', fontWeight: 500 }}>
      {c.name}
    </h3>
    <div className="flex items-center justify-between text-[11px]">
      <span style={{ color: 'rgba(245,240,232,0.55)' }}>{c.assets_published}/{c.assets_total} published</span>
      <ArrowRight size={12} style={{ color: 'rgba(245,240,232,0.4)' }} />
    </div>
  </Link>
);

export default function CampaignAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/campaigns/analytics/overview`);
        setData(d);
      } catch {
        setData({ ok: false });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <DashboardLayout><div className="py-32 text-center text-sm" style={{ color: 'rgba(245,240,232,0.4)' }}>Loading analytics…</div></DashboardLayout>;
  }
  if (!data || !data.ok) {
    return <DashboardLayout><div className="py-20 text-center text-sm" style={{ color: 'rgba(245,240,232,0.55)' }}>No analytics yet — launch a campaign first.</div></DashboardLayout>;
  }

  const t = data.totals || {};
  const r = data.recent || {};
  const cards = data.campaigns || [];
  const empty = (t.campaigns_total || 0) === 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="campaign-analytics-page">
        {/* Header */}
        <div>
          <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-1.5" style={{ color: LIME }}>Performance Pulse</p>
          <h1 className="text-3xl sm:text-4xl" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 500 }}>
            How your campaigns are landing.
          </h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.5)' }}>
            Live signals from your launched campaigns — assets, publishes, share clicks, momentum.
          </p>
        </div>

        {empty ? (
          <div className="py-12 text-center rounded-3xl" style={{ background: '#0e0e0e', border: '1px dashed rgba(255,255,255,0.08)' }}>
            <Lightning size={32} weight="fill" style={{ color: LIME, margin: '0 auto 12px' }} />
            <p className="text-base mb-4" style={{ color: 'rgba(245,240,232,0.7)', fontFamily: "'Instrument Serif', serif" }}>No campaigns running yet.</p>
            <Link to="/dashboard/strategy" data-testid="analytics-empty-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold"
              style={{ background: LIME, color: '#0a0a0a' }}>
              Launch your first campaign <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <>
            {/* 4 momentum tiles */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-metrics">
              <MetricTile label="Active campaigns" value={t.campaigns_active}
                sub={`${t.campaigns_total} total ever launched`}
                accent={LIME} icon={Briefcase} />
              <MetricTile label="Assets created · 7d" value={r.assets_created_7d}
                sub={`${r.assets_created_30d} created in last 30 days`}
                accent={LIME} icon={FileText} />
              <MetricTile label="Published · 7d" value={r.assets_published_7d}
                sub={`${t.assets_published} published all-time`}
                accent={SKY} icon={PaperPlaneTilt} />
              <MetricTile label="Share clicks · 7d" value={r.share_clicks_7d}
                sub={`${t.share_clicks_total} total share clicks`}
                accent="#ff9a76" icon={CursorClick} />
            </div>

            {/* Timeseries + Platforms */}
            <div className="grid lg:grid-cols-[1.4fr,1fr] gap-5">
              <Sparkline days={data.timeseries_14d || []} />
              <PlatformBars rows={data.by_platform || []} />
            </div>

            {/* Recent campaigns */}
            <div>
              <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: 'rgba(245,240,232,0.5)' }}>Recent campaigns</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="analytics-campaign-cards">
                {cards.map((c) => <CampaignCard key={c.id} c={c} />)}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
